import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "../components/layout/Seo";
import { PageHero } from "../components/layout/PageHero";
import { CategoryTile } from "../components/home/ShopTeaser";
import { Eyebrow } from "../components/motion/Reveal";
import { shopCategories } from "../content/site";
import { api, apiReady, formatApiError } from "../lib/api";

const NotifyForm = () => {
  const [email, setEmail] = useState("");
  const [interests, setInterests] = useState([]);
  const [hp, setHp] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  const toggle = (n) => setInterests((x) => (x.includes(n) ? x.filter((i) => i !== n) : [...x, n]));

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) { setError("Please enter a valid email address."); return; }
    setError("");
    if (!apiReady) { toast.error("Launch notifications are not connected on this deployment yet."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/shop/subscribe", { email: email.trim(), interests, website: hp });
      setDone(data?.status ?? "subscribed");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-10 flex items-center gap-4 rounded-2xl border border-orange-500/40 bg-orange-500/10 p-5" data-testid="shop-notify-success">
        <span className="grid h-10 w-10 place-items-center rounded-full bg-orange-500 text-black"><Check className="h-5 w-5" /></span>
        <p className="text-stone-100">{done === "already_subscribed" ? "You're already on the list — we've updated your interests." : "You're on the list. We'll let you know when Care-Shop opens."}</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="mt-10 max-w-xl" data-testid="shop-notify-form">
      <label htmlFor="notify-email" className="font-mono text-[11px] uppercase tracking-[0.22em] text-stone-400">Notify me at launch</label>
      <div className="mt-3 flex flex-col gap-2 rounded-[22px] border border-white/15 bg-white/5 p-2 sm:flex-row sm:rounded-full">
        <input id="notify-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email"
          className="h-12 flex-1 rounded-full bg-transparent px-5 text-stone-100 outline-none placeholder:text-stone-500" data-testid="shop-notify-email" />
        <button type="submit" disabled={busy} className="tc-btn-primary inline-flex h-12 items-center justify-center gap-2 rounded-full px-6 text-sm font-semibold" data-testid="shop-notify-submit">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Keep me posted
        </button>
      </div>
      <input tabIndex={-1} aria-hidden="true" autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} className="absolute -left-[9999px] h-px w-px" />
      {error && <p className="mt-2 text-sm text-red-400" role="alert" data-testid="shop-notify-error">{error}</p>}
      <p className="mt-5 text-xs text-stone-400">Interested in (optional):</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {shopCategories.map((c) => (
          <button type="button" key={c.name} aria-pressed={interests.includes(c.name)} onClick={() => toggle(c.name)} data-testid={`shop-interest-${c.name.toLowerCase().replace(/\W+/g, "-")}`}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${interests.includes(c.name) ? "border-orange-500 bg-orange-500 text-black" : "border-white/15 text-stone-300 hover:border-orange-500"}`}>
            {c.name}
          </button>
        ))}
      </div>
      <p className="mt-5 text-xs leading-relaxed text-stone-400">We'll only use your email to tell you about the Care-Shop launch.</p>
    </form>
  );
};

export default function Shop() {
  return (
    <>
      <Seo title="Care-Shop — Coming Soon | TechCare Technologies" path="/shop"
        description="Care-Shop by TechCare Technologies is coming soon: laptops, desktops, printers, networking, drones, CCTV, smart devices and accessories." />
      <PageHero eyebrow="Care-Shop · Coming soon" testid="shop-hero"
        lines={["Technology,", <span key="t" className="tc-copper-text">taken care of.</span>]}
        lead="We're preparing a store for business and personal technology, selected and supported by the TechCare team. The shop is not open yet — no products are available to buy.">
        <NotifyForm />
      </PageHero>
      <section className="bg-[#0C0A09] pb-24 text-stone-100 sm:pb-32">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
          <div className="flex flex-col justify-between gap-4 border-t border-white/10 pt-10 sm:flex-row sm:items-end">
            <Eyebrow>Planned categories</Eyebrow>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">Preview only · No stock, pricing or checkout</p>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {shopCategories.map((c, i) => <CategoryTile key={c.name} c={c} i={i} dark />)}
          </div>
          <div className="mt-16 grid gap-px overflow-hidden rounded-[22px] border border-white/10 bg-white/10 md:grid-cols-3" data-testid="shop-promise">
            {[["Curated, not crowded", "A focused range chosen for business and everyday reliability."], ["Installed by TechCare", "Optional setup, configuration and deployment by our team."], ["Cared for after sale", "Support connected to our managed and field services."]].map(([t, d], i) => (
              <div key={t} className="bg-[#0C0A09] p-7">
                <p className="font-mono text-xs text-orange-400">0{i + 1}</p>
                <p className="mt-6 font-display text-xl text-stone-100">{t}</p>
                <p className="mt-2 text-sm leading-relaxed text-stone-400">{d}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-stone-400">Planned features — Care-Shop is not yet open and no products are for sale.</p>
        </div>
      </section>
    </>
  );
}
